<template>
  <div class="checkout-page">
    <!-- Header -->
    <header class="checkout-header">
      <img
        src="~@/assets/img/photos/product_logo.png"
        alt="商標"
        class="logo"
      />
    </header>

    <!-- 步驟指引 -->
    <div class="step-indicator">
      <span :class="{ active: currentStep === 1 }">1. 填寫資料</span>
      <span :class="{ active: currentStep === 2 }">2. 訂單確認</span>
      <span :class="{ active: currentStep === 3 }">3. 簽約付款</span>
    </div>

    <!-- 購物內容區塊 -->
    <div class="shopping-content">
      <div class="shopping-header">
        購物內容
      </div>

      <!-- 商品資訊 -->
      <div class="product-info" v-if="selectedProduct">
        <img
          :src="selectedProduct.photo"
          alt="方案圖片"
          class="product-image"
        />
        <div class="product-details">
          <p class="product-name">{{ selectedProduct.name }}</p>
          <p class="product-price">NT ${{ selectedProduct.price }}</p>
        </div>
      </div>

      <!-- 預設商品 (入會費) -->
      <div
        class="product-info"
        v-if="needsMembershipFee && membershipFeeProduct"
      >
        <img
          :src="membershipFeeProduct.photo"
          alt="入會費圖片"
          class="product-image"
        />
        <div class="product-details">
          <p class="product-name">{{ membershipFeeProduct.name }}</p>
          <p class="product-description">(首次入會收取，正式會員免收入會費)</p>
          <p class="product-price">NT ${{ membershipFeeProduct.price }}</p>
        </div>
      </div>

      <!-- 折扣碼 -->
      <div class="discount-code">
        <label for="discount">折扣碼</label>
        <input
          type="text"
          id="discount"
          v-model="discountCode"
          placeholder="請輸入折扣碼"
        />
        <button @click="applyDiscount">套用</button>
        <!-- 成功套用提示 -->
        <p v-if="discountMessage" class="discount-message">
          {{ discountMessage }}
        </p>
      </div>

      <!-- 金額計算 -->
      <div class="amount-summary">
        <div class="amount-item">
          <span>小計：</span>
          <span>{{ subtotal }} 元</span>
        </div>
        <div class="amount-item">
          <span>會員等級折扣：</span>
          <span>-{{ memberDiscountAmount }} 元</span>
        </div>
        <div class="amount-item">
          <span>折扣碼折扣：</span>
          <span>-{{ discountAmount }} 元</span>
        </div>
        <div class="amount-item total">
          <span>合計：</span>
          <span>NT${{ totalAmount }} 元</span>
        </div>
      </div>
    </div>

    <!-- 會員專區 -->
    <div class="member-section">
      <h3>會員專區</h3>
      <div v-if="isLoggedIn" class="member-info">
        <div class="form-group">
          <label for="member-name">姓名</label>
          <input
            id="member-name"
            type="text"
            :value="memberInfo.name"
            readonly
          />
        </div>
        <div class="form-group">
          <label for="member-birthday">生日</label>
          <input
            id="member-birthday"
            type="text"
            :value="memberInfo.birthday"
            readonly
          />
        </div>
        <div class="form-group">
          <label for="member-phone">手機</label>
          <input
            id="member-phone"
            type="text"
            :value="memberInfo.phone"
            readonly
          />
        </div>
        <div class="form-group">
          <label for="member-email">Email</label>
          <input
            id="member-email"
            type="email"
            :value="memberInfo.email"
            readonly
          />
        </div>
        <div class="form-group">
          <label for="member-level">會員等級</label>
          <input
            id="member-level"
            type="text"
            :value="memberInfo.level_name"
            readonly
          />
        </div>
      </div>
      <div v-else>
        <p>會員購買（已繳入會費，登入後自動減免）</p>
        <button @click="openLoginModal">登入</button>
        <p>非會員購買（首次註冊入會費 350 元）</p>
        <button @click="openRegisterModal">註冊</button>
      </div>
    </div>

    <!-- 按鈕區 -->
    <div class="action-buttons">
      <button @click="goBack">返回</button>
      <button :disabled="!isLoggedIn" @click="createTemporaryOrder">
        下一步
      </button>
    </div>

    <!-- 登入 Modal -->
    <div v-if="showLoginModal" class="modal">
      <div class="modal-content">
        <h3>登入</h3>
        <label for="login-phone">手機號碼</label>
        <input type="text" id="login-phone" v-model="loginPhone" />
        <label for="login-password">密碼</label>
        <input type="password" id="login-password" v-model="loginPassword" />
        <button @click="login">登入</button>
        <button @click="closeLoginModal">取消</button>
      </div>
    </div>

    <!-- 註冊 Modal -->
    <div v-if="showRegisterModal" class="modal">
      <div class="modal-content">
        <h3>註冊</h3>
        <div class="form-group">
          <label for="register-name">姓名</label>
          <input type="text" id="register-name" v-model="registerName" />
        </div>
        <div class="form-group">
          <label for="register-gender">性別</label>
          <select id="register-gender" v-model="registerGender">
            <option value="0">女</option>
            <option value="1">男</option>
          </select>
        </div>
        <div class="form-group">
          <label for="register-birthday">生日</label>
          <input
            type="date"
            id="register-birthday"
            v-model="registerBirthday"
          />
        </div>
        <div class="form-group">
          <label for="register-phone">手機</label>
          <input type="text" id="register-phone" v-model="registerPhone" />
        </div>
        <div class="form-group">
          <label for="register-email">Email</label>
          <input type="email" id="register-email" v-model="registerEmail" />
        </div>
        <div class="form-group">
          <label for="register-password">密碼</label>
          <input
            type="password"
            id="register-password"
            v-model="registerPassword"
          />
        </div>
        <div class="form-group">
          <label for="register-confirm-password">確認密碼</label>
          <input
            type="password"
            id="register-confirm-password"
            v-model="registerConfirmPassword"
          />
        </div>
        <button @click="register">註冊</button>
        <button @click="closeRegisterModal">取消</button>
      </div>
    </div>

    <!-- MessageModal -->
    <div>
      <MessageModal ref="messageModal" @closed="handleModalClosed" />
    </div>
  </div>
</template>

<script>
import {
  fetchCheckoutData,
  fetchMembershipFee,
  createTemporaryOrder
} from '@/api/checkout';
import { validateDiscountCode } from '@/api/discount_code';
import { login, register } from '@/api/shopping_auth';
import MessageModal from '@/components/Modal/MessageModal';

export default {
  components: {
    MessageModal
  },
  props: {
    program_id: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      currentStep: 1,
      selectedProduct: null,
      discountCode: '',
      discountAmount: 0,
      discount_code_id: null,
      discountMessage: '',
      subtotal: 0,
      totalAmount: 0,
      isLoggedIn: false,
      memberInfo: {
        id: null,
        name: '',
        birthday: '',
        phone: '',
        email: '',
        branch_id: null,
        type: null,
        level_name: ''
      },
      showLoginModal: false,
      showRegisterModal: false,
      loginPhone: '',
      loginPassword: '',
      registerName: '',
      registerGender: '0', // 預設性別為女
      registerBirthday: '',
      registerPhone: '',
      registerEmail: '',
      registerPassword: '',
      registerConfirmPassword: '',
      membershipFee: 350, // 固定入會費
      membershipFeeProduct: null, // 儲存從 API 獲取的入會費商品資訊
      needsMembershipFee: true, // 判斷是否需要入會費
      memberDiscountAmount: 0, // 會員等級折扣金額
      redirectAfterModal: false, // 關閉彈窗後是否要導頁
      redirectUrl: '' // 導向網址
    };
  },
  methods: {
    async fetchMembershipFee() {
      try {
        const feeData = await new Promise((resolve, reject) => {
          fetchMembershipFee(
            data => resolve(data),
            error => reject(error)
          );
        });
        this.membershipFeeProduct = feeData; // 儲存入會費資訊
        this.membershipFee = feeData.price; // 更新金額
        this.calculateTotal(); // 重新計算總金額
      } catch (error) {
        console.error('Failed to fetch membership fee:', error);
      }
    },
    fetchProductDetails() {
      fetchCheckoutData(
        this.program_id,
        data => {
          this.selectedProduct = data;
          this.calculateTotal();
        },
        error => {
          console.error('Failed to fetch product details:', error);
        }
      );
    },
    calculateTotal() {
      // 方案價格
      const productPrice = this.selectedProduct
        ? this.selectedProduct.price
        : 0;

      // 計算入會費
      const membershipFee =
        this.needsMembershipFee && this.membershipFeeProduct
          ? this.membershipFeeProduct.price
          : 0;

      // 小計 = 商品價格 + 入會費
      this.subtotal = productPrice + membershipFee;

      // 會員等級折扣 (只對 方案計算，不影響入會費)
      this.memberDiscountAmount = 0; // 重置會員折扣
      let discountedProductPrice = productPrice;
      if (this.isLoggedIn && this.memberInfo.level_discount) {
        const discountValue =
          productPrice * (1 - this.memberInfo.level_discount);
        this.memberDiscountAmount = Math.round(discountValue); // 會員折扣
        discountedProductPrice = productPrice - this.memberDiscountAmount; // 折扣後價格
      }

      // 折扣碼折扣 (只對會員折扣後的商品金額)
      if (this.discountCode && this.discountAmount > 0) {
        if (this.discountType === 0) {
          // 固定金額折扣，最多扣到會員折扣後的商品金額
          this.discountAmount = Math.min(
            this.discountAmount,
            discountedProductPrice
          );
        } else if (this.discountType === 1) {
          // 百分比折扣
          this.discountAmount = Math.round(
            (discountedProductPrice * this.discountValue) / 100
          );
        }
      }

      // 總計 = 會員折扣後的商品金額 - 折扣碼折扣 + 入會費
      this.totalAmount =
        discountedProductPrice - this.discountAmount + membershipFee;
    },
    async applyDiscount() {
      if (!this.isLoggedIn) {
        this.showError('請先登入再使用折扣碼');
        return;
      }
      // 驗證折扣碼格式
      const discountPattern = /^[a-zA-Z0-9]+$/;
      if (!this.discountCode.trim()) {
        this.showError('請輸入折扣碼');
        return;
      }
      if (!discountPattern.test(this.discountCode.trim())) {
        this.showError('折扣碼只能包含英文和數字');
        return;
      }

      const params = {
        program_id: this.program_id,
        member_id: this.memberInfo.member_id,
        discount_code: this.discountCode.trim()
      };

      try {
        const discountData = await new Promise((resolve, reject) => {
          validateDiscountCode(
            params,
            data => resolve(data),
            errorMessage => reject(errorMessage)
          );
        });

        // 保存折扣碼的類型與值
        this.discountType = discountData.type;
        this.discountValue = discountData.discount_value;
        this.discount_code_id = discountData.discount_code_id;

        // 計算折扣金額
        const productPriceForDiscount = this.selectedProduct
          ? this.selectedProduct.price
          : 0;

        if (discountData.type === 0) {
          // 固定金額折扣
          this.discountAmount = Math.min(
            discountData.discount_value,
            productPriceForDiscount
          );
        } else if (discountData.type === 1) {
          // 百分比折扣
          this.discountAmount = Math.round(
            (productPriceForDiscount * discountData.discount_value) / 100
          );
        }

        // 更新總金額
        this.calculateTotal();

        this.discountMessage = `成功套用 ${discountData.discount_code_name} 折扣碼！`; // 套用成功提示文字
        this.showSuccess('折扣碼套用成功！');
      } catch (error) {
        // 如果折扣碼無效，清除之前的折扣碼和折扣金額
        this.discountCode = ''; // 清空折扣碼輸入框
        this.discount_code_id = null; // 清空折扣碼 ID
        this.discountAmount = 0; // 清除折扣金額
        this.totalAmount = this.subtotal; // 恢復總計為原始金額
        this.discountMessage = ''; // 清空提示文字
        this.showError(error || '折扣碼無效');
      }
    },
    openLoginModal() {
      this.showLoginModal = true;
    },
    closeLoginModal() {
      this.showLoginModal = false;

      // 重置表單
      this.loginPhone = '';
      this.loginPassword = '';
    },
    openRegisterModal() {
      this.showRegisterModal = true;
    },
    closeRegisterModal() {
      this.showRegisterModal = false;

      // 重置註冊表單數據
      this.registerName = '';
      this.registerBirthday = '';
      this.registerPhone = '';
      this.registerEmail = '';
      this.registerPassword = '';
      this.registerConfirmPassword = '';
    },
    async login() {
      try {
        const params = {
          phone: this.loginPhone.trim(),
          password: this.loginPassword
        };
        const memberData = await new Promise((resolve, reject) => {
          login(
            params,
            data => resolve(data),
            error => reject(error)
          );
        });
        this.memberInfo = memberData;
        this.isLoggedIn = true;

        // 登入後判斷是否需要入會費
        this.needsMembershipFee = this.memberInfo.type === 3;

        // 登入後重新計算總計
        this.calculateTotal();
        this.closeLoginModal();
        this.showSuccess('登入成功！');
      } catch (error) {
        this.showError(error || '登入失敗，請檢查帳號或密碼是否正確');
      }
    },
    async register() {
      try {
        if (!this.registerName.trim()) {
          this.showError('請輸入姓名');
          return;
        }
        if (!this.registerBirthday) {
          this.showError('請選擇生日');
          return;
        }
        if (!/^\d{10}$/.test(this.registerPhone)) {
          this.showError('請輸入有效的手機號碼');
          return;
        }
        if (
          !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
            this.registerEmail
          )
        ) {
          this.showError('請輸入有效的 Email');
          return;
        }
        if (!this.registerPassword.trim()) {
          this.showError('請輸入密碼');
          return;
        }
        if (this.registerPassword !== this.registerConfirmPassword) {
          this.showError('密碼與確認密碼不一致');
          return;
        }

        const params = {
          name: this.registerName.trim(),
          gender: this.registerGender,
          birthday: this.registerBirthday,
          phone: this.registerPhone.trim(),
          email: this.registerEmail.trim(),
          password: this.registerPassword
        };

        await new Promise((resolve, reject) => {
          register(
            params,
            () => resolve(),
            error => reject(error)
          );
        });

        await this.autoLogin(this.registerPhone.trim(), this.registerPassword);
        this.closeRegisterModal();
      } catch (error) {
        this.showError(error || '註冊失敗，請稍後再試');
      }
    },
    async autoLogin(phone, password) {
      try {
        const params = { phone, password };
        const memberData = await new Promise((resolve, reject) => {
          login(
            params,
            data => resolve(data),
            error => reject(error)
          );
        });

        this.memberInfo = memberData;
        this.isLoggedIn = true;

        // 登入後判斷是否需要入會費
        this.needsMembershipFee = this.memberInfo.type === 3;

        // 登入後重新計算總計
        this.calculateTotal();
        this.showSuccess('註冊成功，已自動登入！');
      } catch (error) {
        this.showError(error || '自動登入失敗，請手動登入');
      }
    },
    goBack() {
      this.$router.push({ name: 'ProductPlan' });
    },
    async createTemporaryOrder() {
      /* =====================================================
       * 檢查 email、name、birthday 是否為空
       * ===================================================== */
      const { email, name, birthday } = this.memberInfo;
      const isMissingEmail = !email || !email.trim();
      const isMissingName = !name || !name.trim();
      const isMissingBirthday = !birthday || !birthday.trim();

      if (isMissingEmail || isMissingName || isMissingBirthday) {
        // 彈窗訊息根據缺漏動態組合
        const lackArr = [];
        if (isMissingEmail) lackArr.push('Email');
        if (isMissingName) lackArr.push('姓名');
        if (isMissingBirthday) lackArr.push('生日');
        const lackText = lackArr.join('、');

        this.showError(
          `您的會員資料不完整（缺少：${lackText}），` +
            '請至 App 會員中心補填後再繼續操作。\n' +
            '若仍無法下一步，請聯繫客服協助處理。',
          'https://www.whayoga.com/pages/whayogaapp'
        );
        return;
      }

      // 構建購物車數據
      const cartDetails = {
        programs: [
          {
            program_id: this.selectedProduct.program_id,
            name: this.selectedProduct.name,
            price: this.selectedProduct.price,
            count: 1, // 方案數量固定為 1
            discount: this.discountAmount + this.memberDiscountAmount,
            subtotal: this.totalAmount,
            discount_type: '0'
          }
        ],
        products: this.needsMembershipFee
          ? [
              {
                product_id: this.membershipFeeProduct.id,
                name: this.membershipFeeProduct.name,
                price: this.membershipFeeProduct.price,
                count: 1,
                discount: 0,
                subtotal: this.membershipFeeProduct.price,
                discount_type: '0'
              }
            ]
          : [],
        subtotal: this.subtotal,
        total: this.totalAmount,
        level_discount_amount: this.memberDiscountAmount,
        discount_code_amount: this.discountAmount
      };

      // 組裝 API 請求數據
      const requestData = {
        type: 0, // 購物網頁類型
        member_id: this.memberInfo.member_id,
        cart_details: cartDetails,
        subtotal: this.subtotal,
        discount: this.discountAmount + this.memberDiscountAmount,
        total: this.totalAmount,
        membership_fee: this.needsMembershipFee ? 1 : 0,
        discount_code_id: this.discount_code_id || null // 傳遞折扣碼 ID
      };

      try {
        // 呼叫 API 並獲取 `token`
        const token = await new Promise((resolve, reject) => {
          createTemporaryOrder(
            requestData,
            data => resolve(data.token),
            reject
          );
        });

        // 跳轉到訂單確認頁面，附加 token
        this.$router.push(`/order-confirmation/${token}`);
      } catch (error) {
        this.showError(error || '建立訂單失敗，請稍後再試');
      }
    },
    showError(message, redirect = null) {
      this.$refs.messageModal.show(message, '錯誤', 'error');
      this.redirectAfterModal = !!redirect;
      this.redirectUrl = redirect || '';
    },
    showSuccess(message) {
      this.$refs.messageModal.show(message, '成功', 'success');
    },
    // 處理 Modal 關閉事件
    handleModalClosed() {
      if (this.redirectAfterModal && this.redirectUrl) {
        window.open(this.redirectUrl, '_blank');
        setTimeout(() => location.reload(), 300);
      }
      // 重置旗標
      this.redirectAfterModal = false;
      this.redirectUrl = '';
    }
  },
  mounted() {
    this.fetchMembershipFee(); // 獲取入會費
    this.fetchProductDetails(); // 獲取商品資訊
    document.title = '昱驊養生瑜珈中心-線上購課';
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/page/Checkout.scss';
</style>
